import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  Avatar,
  CircularProgress,
  Fade,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { chatBotService } from '@services/ChatBot/ChatBotService';

const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  width: 400,
  height: 600,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 9999,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  borderRadius: 0,
  overflow: 'hidden'
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5)
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  maxWidth: '80%'
}));

const MessageContent = styled(Paper)(({ theme, isUser }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: isUser ? '#667eea' : 'white',
  color: isUser ? 'white' : '#333',
  borderRadius: 0,
  wordWrap: 'break-word',
  fontSize: '0.9rem',
  lineHeight: 1.6,
  // Markdown styling
  '& p': {
    margin: '0.5em 0',
    '&:first-of-type': { marginTop: 0 },
    '&:last-child': { marginBottom: 0 }
  },
  '& strong': {
    fontWeight: 600,
    color: isUser ? 'white' : '#667eea',
    backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : 'rgba(102, 126, 234, 0.08)',
    padding: '0 3px',
    borderRadius: 0
  },
  '& em': {
    fontStyle: 'italic',
    color: isUser ? 'rgba(255,255,255,0.95)' : '#666',
    fontWeight: 500
  },
  '& ul, & ol': {
    margin: '0.5em 0',
    paddingLeft: '1.5em',
    listStyle: 'none'
  },
  '& ul li': {
    position: 'relative',
    marginBottom: '0.3em',
    paddingLeft: '1.2em',
    '&:before': {
      content: '"•"',
      position: 'absolute',
      left: 0,
      color: isUser ? 'white' : '#667eea',
      fontWeight: 'bold'
    }
  },
  '& ol': {
    counterReset: 'list-counter',
    listStyle: 'none'
  },
  '& ol li': {
    position: 'relative',
    marginBottom: '0.3em',
    paddingLeft: '1.5em',
    counterIncrement: 'list-counter',
    '&:before': {
      content: 'counter(list-counter) "."',
      position: 'absolute',
      left: 0,
      color: isUser ? 'white' : '#667eea',
      fontWeight: 600
    }
  },
  '& code': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(102, 126, 234, 0.1)',
    padding: '2px 6px',
    borderRadius: 0,
    fontFamily: 'monospace',
    fontSize: '0.85em'
  },
  '& pre': {
    backgroundColor: isUser ? 'rgba(0,0,0,0.2)' : '#f5f5f5',
    padding: '8px 12px',
    borderRadius: 0,
    overflow: 'auto',
    margin: '0.5em 0'
  },
  '& a': {
    color: isUser ? 'white' : '#667eea',
    textDecoration: 'underline'
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'white',
  borderTop: '1px solid #e0e0e0',
  display: 'flex',
  gap: theme.spacing(1)
}));

const FloatingButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: 40,
  right: 30,
  width: 70,
  height: 70,
  backgroundColor: '#1c9ad6',
  color: 'white',
  boxShadow: '0 4px 20px rgba(28, 154, 214, 0.4)',
  '&:hover': {
    backgroundColor: '#1c9ad6',
    transform: 'scale(1.1)'
  },
  '&:active': {
    transform: 'scale(0.95)'
  },
  transition: 'all 0.2s ease',
  zIndex: 9998
}));

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Xin chào! Tôi là trợ lý ảo của hệ thống GSWM. Tôi có thể giúp bạn tra cứu:\n\n**📊 2 Loại dữ liệu tiêu thụ:**\n\n**1️⃣ Tiêu thụ theo NGÀY** (Nhà máy)\n\n• Tiêu thụ hàng ngày của các Nhà máy\n\n• VD: "Tiêu thụ hôm nay của HP7"\n\n**2️⃣ Tiêu thụ theo KỲ CƯỚC** (Khách hàng)\n\n• Chỉ số & tiêu thụ theo tháng của từng KH\n\n• VD: "Tiêu thụ tháng này của Pacific"\n\n**⚠️ Cảnh báo thiết bị:**\n\n• Tra cứu cảnh báo theo mã khách hàng\n\n• VD: "**Cảnh báo 6816947**" hoặc "**Tra cứu cảnh báo 6816947**"\n\n**📋 Khác:**\n\n• Thông tin khách hàng\n\n• Giám sát tiêu thụ\n\nBạn muốn tra cứu gì?',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await chatBotService.sendMessage(inputValue, messages);

      const botMessage = {
        id: messages.length + 2,
        text: response.message,
        isUser: false,
        timestamp: new Date(),
        data: response.data // Dữ liệu từ API nếu có
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: messages.length + 2,
        text: 'Xin lỗi, đã có lỗi xảy ra. Vui lòng thử lại sau.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {!isOpen && (
        <Tooltip title="Chat với trợ lý AI" placement="left">
          <FloatingButton onClick={() => setIsOpen(true)}>
            <svg
              height="50px"
              width="50px"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 508 508">
              <circle style={{ fill: '#54C0EB' }} cx="254" cy="254" r="254" />
              <path
                style={{ fill: '#FFFFFF' }}
                d="M303.7,303.3c30.5-17.3,51-50.1,51-87.6c0-55.7-45.1-100.8-100.8-100.8S153.2,160,153.2,215.6
                c0,37.6,20.6,70.3,51,87.6C141,319.3,89.7,365,66,424.8c46.5,51.1,113.5,83.2,188,83.2s141.5-32.1,188-83.2
                C418.3,365,367,319.3,303.7,303.3z"
              />
              <path
                style={{ fill: '#324A5E' }}
                d="M401.6,182.3h-15.8C370.9,123.4,317.5,79.6,254,79.6s-116.9,43.7-131.8,102.7h-15.8
                c-5.4,0-9.8,4.4-9.8,9.8V240c0,5.4,4.4,9.8,9.8,9.8h20c6.1,0,10.8-5.5,9.7-11.4c-2-10.4-2.7-21.3-1.8-32.5
                c4.8-59.3,53.6-106.9,113.1-110.1c69.2-3.8,126.8,51.5,126.8,119.9c0,7.8-0.8,15.3-2.2,22.7c-1.2,6,3.6,11.5,9.6,11.5h1.8
                c-4.2,13-14.9,37.2-38.3,50.2c-19.6,10.9-44.3,11.9-73.4,2.8c-1.5-6.7-8.9-14.6-16.5-18.3c-9.8-4.9-15.9-0.8-19.4,6.2
                s-3,14.3,6.7,19.2c8.6,4.3,21.6,5.2,27,0.5c13.9,4.3,26.9,6.5,39,6.5c15,0,28.5-3.3,40.4-10c27.5-15.3,38.8-43.7,42.8-57.2h9.9
                c5.4,0,9.8-4.4,9.8-9.8v-47.9C411.4,186.7,407,182.3,401.6,182.3z"
              />
            </svg>
          </FloatingButton>
        </Tooltip>
      )}

      {isOpen && (
        <Fade in={isOpen}>
          <ChatContainer>
            <ChatHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <SmartToyIcon />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Trợ lý AI GSWM
                  </Typography>
                  {/* <Typography variant="caption">Trợ lý thông minh</Typography> */}
                </Box>
              </Box>
              <IconButton onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </ChatHeader>

            <MessagesContainer>
              {messages.map((message) => (
                <MessageBubble key={message.id} isUser={message.isUser}>
                  {!message.isUser && (
                    <Avatar sx={{ bgcolor: '#667eea', width: 32, height: 32 }}>
                      <SmartToyIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                  <MessageContent isUser={message.isUser} elevation={1}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                    {message.data && (
                      <Box mt={1} sx={{ opacity: 0.8 }}>
                        <Typography variant="caption">📊 Dữ liệu đã được tra cứu</Typography>
                      </Box>
                    )}
                  </MessageContent>
                  {message.isUser && (
                    <Avatar sx={{ bgcolor: '#764ba2', width: 32, height: 32 }}>
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                </MessageBubble>
              ))}
              {isLoading && (
                <MessageBubble isUser={false}>
                  <Avatar sx={{ bgcolor: '#667eea', width: 32, height: 32 }}>
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <MessageContent isUser={false}>
                    <CircularProgress size={20} sx={{ color: '#667eea' }} />
                    <Typography variant="caption" ml={1}>
                      Đang suy nghĩ...
                    </Typography>
                  </MessageContent>
                </MessageBubble>
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputContainer>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                placeholder="Nhập câu hỏi của bạn..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0
                  }
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                sx={{
                  bgcolor: '#667eea',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#764ba2'
                  },
                  '&:disabled': {
                    bgcolor: '#ccc'
                  }
                }}>
                <SendIcon />
              </IconButton>
            </InputContainer>
          </ChatContainer>
        </Fade>
      )}
    </>
  );
};

export default ChatBot;
